/**
 * Responsibility: likes, saved searches and settings from Firestore, in the
 * shapes the pages already expect.
 *
 * The same adapter pattern as `useDurableCollection`: read what Firestore
 * holds, hand back what the page was written against. Changing the components
 * to a second shape would be a large diff proving nothing, and would leave two
 * shapes for the same thing.
 *
 * **`watching` is deliberately empty here.** The watched list is not one of a
 * person's own documents - it is the shared poll target in SQLite joined
 * against their subscriptions, so the hub is the only thing that can produce
 * it. An empty array is honest; a stale one pretending to be current is not,
 * and the offline banner already says the analysis half is missing.
 *
 * Money stays text, because Firestore numbers are doubles and 19.99 stored as
 * one is not 19.99 coming back.
 */

import { useMemo } from 'react';

import type { Saved, UserSettings } from '../client';
import {
  useLiveLikes,
  useLiveSearches,
  useLiveSettings,
  type StoredLike,
  type StoredSearch,
} from '../../firestore/useDurable';
import type { WithId } from '../../firestore/useLive';

function toLike(row: WithId<StoredLike>) {
  return {
    product_key: row.product_key,
    name: row.name,
    category: row.category,
    url: row.url,
    image_url: row.image_url ?? null,
    baseline_value: row.baseline_value ?? null,
    baseline_label: row.baseline_label ?? null,
    notes: row.notes ?? '',
    liked_at: new Date(0).toISOString(),
  };
}

function toSearch(row: WithId<StoredSearch>) {
  return {
    id: row.id,
    label: row.label,
    query: row.query,
    mode: row.mode,
    category: row.category ?? null,
    min_raw: null,
    max_raw: null,
    min_value: null,
    created_at: new Date(0).toISOString(),
    last_run_at: null,
    preview_images: null,
    result_count: row.result_count ?? null,
  };
}

/** Likes and saved searches as Firestore holds them, or undefined while loading. */
export function useDurableSaved(): { data: Saved | undefined; stale: boolean } {
  const likes = useLiveLikes();
  const searches = useLiveSearches();

  return useMemo(() => {
    if (!likes.data && !searches.data) {
      return { data: undefined, stale: likes.stale };
    }
    return {
      data: {
        likes: (likes.data ?? []).map(toLike),
        searches: (searches.data ?? []).map(toSearch),
        watching: [],
      } as unknown as Saved,
      stale: likes.stale || searches.stale,
    };
  }, [likes.data, likes.stale, searches.data, searches.stale]);
}

/**
 * The grading assumptions, from Firestore.
 *
 * These drive every payoff figure on two boards, so reading them without the
 * hub is what keeps those pages explicable rather than silently using
 * somebody else's defaults.
 */
export function useDurableSettings(): { data: UserSettings | undefined; stale: boolean } {
  const settings = useLiveSettings();

  return useMemo(() => {
    if (!settings.data) return { data: undefined, stale: settings.stale };
    return {
      data: {
        grading_cost_per_card: settings.data.grading_cost_per_card,
        grading_submission_shipping: settings.data.grading_submission_shipping,
        grading_sell_fee_rate: settings.data.grading_sell_fee_rate,
        grading_cards_bought: settings.data.grading_cards_bought,
        updated_at: new Date(0).toISOString(),
      } as unknown as UserSettings,
      stale: settings.stale,
    };
  }, [settings.data, settings.stale]);
}
